import { FirebaseApp } from "@firebase/app";
import {
  AuthActionResponse,
  AuthProvider,
  CheckResponse,
  IdentityResponse,
  OnErrorResponse,
} from "@refinedev/core";
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  browserSessionPersistence,
  browserPopupRedirectResolver,
  getAuth,
  getIdTokenResult,
  ParsedToken,
  signOut,
  User as FirebaseUser,
  signInWithPopup,
  linkWithPopup,
  updateEmail,
  sendEmailVerification,
} from "@firebase/auth";
import * as Sentry from "@sentry/react";
import { IAuthCallbacks } from "refine-firebase/lib/interfaces";
import { IProfileV1 } from "../../interfaces/user";
import { api, apiJson, apiVersion, hasAuthCookies, setCookiesForWildcardDomain, deleteCookie } from "../api";
import { authLogger } from "../logger";
import { trackAuthStateChange } from "../sentry-breadcrumbs";

export class GetLostFirebaseAuth {
  auth: Auth;
  private isAuthInitialized = false;
  private authInitPromise: Promise<void> | null = null;

  constructor(
    private readonly authActions?: IAuthCallbacks,
    firebaseApp?: FirebaseApp,
    auth?: Auth
  ) {
    this.auth = auth || getAuth(firebaseApp);
    this.auth.useDeviceLanguage();

    this.getAuthProvider = this.getAuthProvider.bind(this);
    this.handleLogIn = this.handleLogIn.bind(this);
    this.handleLogOut = this.handleLogOut.bind(this);
    this.getUserIdentity = this.getUserIdentity.bind(this);
    this.handleCheckAuth = this.handleCheckAuth.bind(this);
    this.getPermissions = this.getPermissions.bind(this);
    this.onError = this.onError.bind(this);
    this.getFirebaseUser = this.getFirebaseUser.bind(this);
    this.waitForAuthInit = this.waitForAuthInit.bind(this);
    
    // Initialize auth state tracking
    this.initializeAuthState();
  }

  private initializeAuthState(): void {
    if (!this.authInitPromise) {
      this.authInitPromise = new Promise((resolve) => {
        const unsubscribe = this.auth?.onAuthStateChanged((user) => {
          this.isAuthInitialized = true;
          unsubscribe();
          resolve();
        });
      });
    }
  }

  private async waitForAuthInit(timeout = 3000): Promise<void> {
    if (this.isAuthInitialized) {
      return Promise.resolve();
    }
    
    return Promise.race([
      this.authInitPromise || Promise.resolve(),
      new Promise<void>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Auth initialization timeout'));
        }, timeout);
      })
    ]);
  }

  public async handleLogOut(params: any): Promise<AuthActionResponse> {
    try {
      // Track auth state change before logout
      trackAuthStateChange("logout", {
        firebaseUid: this.auth?.currentUser?.uid,
      });
      
      // Firebase signout
      await signOut(this.auth);
      
      // Clear Sentry user context
      Sentry.setUser(null);
      
      // Clear all authentication cookies
      deleteCookie('accessToken');
      deleteCookie('refreshToken');
      deleteCookie('refreshTokenIssuedAt');
      
      // Clear localStorage
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem('accessTokenExpiresAt');
          // Clear any other auth-related localStorage items
          Object.keys(window.localStorage).forEach(key => {
            if (key.startsWith('auth_') || key.startsWith('token_') || key.startsWith('firebase_')) {
              window.localStorage.removeItem(key);
            }
          });
        } catch (error) {
          console.warn('Failed to clear localStorage:', error);
        }
      }
      
      // Clear any auth-related sessionStorage
      if (typeof window !== 'undefined' && window.sessionStorage) {
        try {
          Object.keys(window.sessionStorage).forEach(key => {
            if (key.startsWith('auth_') || key.startsWith('token_') || key.startsWith('firebase_')) {
              window.sessionStorage.removeItem(key);
            }
          });
        } catch (error) {
          console.warn('Failed to clear sessionStorage:', error);
        }
      }
      
      authLogger.log('logout', { timestamp: Date.now(), success: true });
      
      await this.authActions?.onLogout?.(this.auth);
      
      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error: any) {
      authLogger.log('logout', { success: false }, error);
      return {
        success: false,
        error: error,
      };
    }
  }

  public async handleLogIn(loginType: any): Promise<AuthActionResponse> {
    try {
      if (this.auth) {
        await this.auth.setPersistence(browserSessionPersistence);

        const providerName = loginType?.providerName as string | undefined;
        const provider =
          providerName === "google"
            ? new GoogleAuthProvider()
            : providerName === "apple"
            ? new OAuthProvider("apple.com")
            : null;

        if (!provider) {
          return { success: false, error: new Error("Unsupported provider") };
        }

        const userCredential = await signInWithPopup(
          this.auth,
          provider,
          browserPopupRedirectResolver
        );

        // Set Sentry user context from Firebase
        Sentry.setUser({
          id: userCredential.user.uid,
        });
        
        // Track auth state change
        trackAuthStateChange("login", {
          provider: providerName,
          firebaseUid: userCredential.user.uid,
        });

        // Ensure admin cookies are set/overwritten based on the freshly signed-in Firebase user
        try {
          await this.ensureAdminSession(true);
        } catch (sessionError: any) {
          // If user needs onboarding (404), propagate this error to be handled by caller
          if (sessionError?.needsOnboarding || sessionError?.status === 404) {
            authLogger.log('login_failed', { providerName, reason: 'needs_onboarding' });
            throw sessionError;
          }
          // For other session errors, rethrow
          throw sessionError;
        }

        authLogger.log('login_success', { providerName });

        this.authActions?.onLogin?.(userCredential.user);

        return {
          success: true,
          redirectTo: "/",
        };
      } else {
        authLogger.log('login_failed', { reason: 'auth_not_initialized' });
        return {
          success: false,
          error: new Error("User not found"),
        };
      }
    } catch (error: any) {
      authLogger.log('login_failed', { providerName: loginType?.providerName }, error);
      
      // Track failed auth
      trackAuthStateChange("login_failed", {
        provider: loginType?.providerName,
        error: error?.message,
      });
      
      // Preserve the error properties for caller to check
      const resultError = new Error(error?.message || 'Login failed');
      (resultError as any).needsOnboarding = error?.needsOnboarding;
      (resultError as any).status = error?.status;
      return {
        success: false,
        error: resultError,
      };
    }
  }

  private async authenticateAdminWithFirebaseToken(
    firebaseIdToken: string,
    phone?: string
  ): Promise<{ accessToken: string; refreshToken: string; expiresIn?: number }> {
    // Call admin auth endpoint directly (avoid api() to prevent refresh recursion)
    const base = import.meta.env.VITE_API;
    const url = `${base}/admin/${apiVersion}/auth`;
    const body: any = {};
    
    // Include phone if provided
    if (phone) {
      body.phone = phone;
    }
    
    const hasBody = Object.keys(body).length > 0;
    const resp = await fetch(url, {
      method: "POST",
      headers: hasBody
        ? { Authorization: `Bearer ${firebaseIdToken}`, "Content-Type": "application/json" }
        : { Authorization: `Bearer ${firebaseIdToken}` },
      body: hasBody ? JSON.stringify(body) : undefined,
    });

    if (!resp.ok) {
      // Check if this is a 404 - user doesn't exist, needs onboarding
      if (resp.status === 404) {
        const error: any = new Error('User not found - onboarding required');
        error.needsOnboarding = true;
        error.status = 404;
        throw error;
      }
      const text = await resp.text().catch(() => "");
      throw new Error(`Admin auth failed (${resp.status}): ${text || resp.statusText}`);
    }

    const data = await resp.json();
    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: typeof data.expires_in === 'number' ? data.expires_in : undefined,
    };
  }

  private async getUserIdentity(): Promise<IdentityResponse> {
    // Ensure admin session is established, then fetch the profile via cookie-based api
    await this.ensureAdminSession();
    const profile = await apiJson<IProfileV1>(`/admin/${apiVersion}/me`);
    
    // Enrich Sentry user context with profile data
    Sentry.setUser({
      id: profile.user_id,
      username: profile.id,
    });
    
    // Set user type tag
    if (profile.type) {
      Sentry.setTag('user_type', profile.type);
    }
    
    return {
      avatar: profile.photo ? profile.photo.url : undefined,
      photoURL: profile.photo ? profile.photo.url : null,
      uid: profile.user_id,
      ...profile
    };
  }

  private getFirebaseUser(immediate = false): Promise<FirebaseUser> {
    // If we want immediate response and auth is already initialized
    if (immediate && this.isAuthInitialized) {
      return Promise.resolve(this.auth?.currentUser as FirebaseUser);
    }
    
    return new Promise<FirebaseUser>((resolve, reject) => {
      const unsubscribe = this.auth?.onAuthStateChanged((user) => {
        unsubscribe();
        resolve(user as FirebaseUser | PromiseLike<FirebaseUser>);
      }, reject);
    });
  }

  private async handleCheckAuth(params: any): Promise<CheckResponse> {
    try {
      // Wait for Firebase auth to initialize before checking auth status
      await this.waitForAuthInit();
      
      // Check current path
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      
      const user = await this.getFirebaseUser();
      if (user) {
        // Set Sentry user context early for error tracking
        Sentry.setUser({
          id: user.uid,
        });
        
        // If we're on the onboarding page, the user has Firebase auth but no backend profile yet
        // Return unauthenticated so they stay on the onboarding page
        if (currentPath === '/onboarding') {
          return { authenticated: false };
        }
        
        try {
          // Always try to ensure admin session on auth check (page reload scenario)
          await this.ensureAdminSession();
          
          // Verify the admin session actually works by making a test API call
          await this.verifyAdminSession();
          
          // Track successful auth check
          trackAuthStateChange("auth_check_success", {
            firebaseUid: user.uid,
          });
          
          return { authenticated: true };
        } catch (error: any) {
          authLogger.log('session_check_failed', { hasUser: !!user }, error as Error);
          
          // Check if user needs onboarding
          if (error?.needsOnboarding || error?.status === 404) {
            authLogger.log('login_failed', { type: 'user_not_found', needsOnboarding: true });
            return { authenticated: false, redirectTo: "/onboarding" };
          }
          
          // Try to refresh the session once more before giving up
          try {
            await this.ensureAdminSession(true); // force refresh
            await this.verifyAdminSession();
            return { authenticated: true };
          } catch (retryError: any) {
            authLogger.log('session_retry_failed', { hasUser: !!user }, retryError as Error);
            
            // Check again if user needs onboarding
            if (retryError?.needsOnboarding || retryError?.status === 404) {
              return { authenticated: false, redirectTo: "/onboarding" };
            }
            
            // Track failed auth check
            trackAuthStateChange("auth_check_failed", {
              firebaseUid: user.uid,
              error: retryError?.message,
            });
            
            return { authenticated: false, logout: true, redirectTo: "/login" };
          }
        }
      }

      // User is definitely not authenticated (after proper initialization)
      return {
        authenticated: false,
        logout: true,
        redirectTo: "/login",
      };
    } catch (error) {
      // Error during auth state check or timeout
      authLogger.log('auth_check_error', {}, error as Error);
      
      // If it's a timeout error, we might still be initializing
      if (error instanceof Error && error.message.includes('timeout')) {
        // Return loading state instead of redirecting
        return { authenticated: false };
      }
      
      return { authenticated: false, logout: true, redirectTo: "/login" };
    }
  }

  public async getPermissions(): Promise<ParsedToken> {
    if (this.auth?.currentUser) {
      const idTokenResult = await getIdTokenResult(this.auth.currentUser);
      return idTokenResult?.claims;
    } else {
      return Promise.reject(new Error("User is not found"));
    }
  }

  public async onError(error: any): Promise<OnErrorResponse> {
    if (error?.code === "auth/invalid-credential") {
      return {
        error: new Error("Invalid credentials"),
        redirectTo: "/login",
      };
    }
    // Backend 404 from handleLogIn (POST /admin/v1/auth) means the
    // Firebase UID has no users row yet — route to /onboarding so
    // the user can complete profile creation. Without this, Refine
    // shows the error AND bounces the user to /login, which is the
    // exact "logged in, then logged out" symptom from the original
    // bug report. See getlst/getlost-admin#119 follow-up.
    if (error?.needsOnboarding === true || error?.status === 404) {
      return {
        error,
        redirectTo: "/onboarding",
      };
    }
    // Backend 5xx / network failures are transient. Bouncing to /login
    // would log the user out for a backend hiccup. Stay on the current
    // route so the user can retry. See getlst/getlost-admin#121.
    if (typeof error?.status === "number" && error.status >= 500) {
      return {
        error,
        // No redirectTo: Refine keeps the user on the current route.
      };
    }
    return {
      error: error,
      redirectTo: "/login",
    };
  }

  // Method to update Firebase user email
  public async updateUserEmail(email: string): Promise<void> {
    const user = this.auth?.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }

    await updateEmail(user, email);
    
    // Send verification email
    await sendEmailVerification(user);
  }

  // Method to send email verification code
  public async sendEmailVerificationCode(email: string): Promise<void> {
    const user = this.auth?.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }

    // First update the email in Firebase
    await updateEmail(user, email);
    
    // Send Firebase verification email
    await sendEmailVerification(user);
  }

  // Method to verify email - Firebase will handle this via email link
  public async verifyEmailAndUpdate(email: string, code: string): Promise<void> {
    const user = this.auth?.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }

    // Reload user to get fresh email verification status
    await user.reload();
    
    // Check if email is verified
    if (user.emailVerified) {
      // Email is verified, we're good
      return;
    }
    
    // If not verified, throw error
    throw new Error('Email not verified yet. Please check your email and click the verification link.');
  }

  // Method to check if current user has email
  public async checkUserHasEmail(): Promise<boolean> {
    const user = await this.getFirebaseUser();
    return Boolean(user?.email);
  }

  // Method to link Google or Apple provider to current user
  public async linkProvider(providerType: 'google' | 'apple'): Promise<void> {
    const user = this.auth?.currentUser;
    if (!user) {
      throw new Error("No user is currently signed in");
    }

    let provider;
    if (providerType === 'google') {
      provider = new GoogleAuthProvider();
    } else if (providerType === 'apple') {
      provider = new OAuthProvider('apple.com');
    } else {
      throw new Error("Invalid provider type");
    }

    try {
      const result = await linkWithPopup(user, provider, browserPopupRedirectResolver);
      
      // After linking, the user's email should be updated from the provider
      // Force refresh to get the updated user info
      await user.reload();
      
      // Get fresh user object to ensure we have latest data
      const currentUser = this.auth.currentUser;
      if (!currentUser?.email) {
        // Provider linked but no email - this is OK for Apple "Hide My Email"
        // The caller will handle requesting email manually
        throw new Error("No email received from provider");
      }
      
      // Establish admin session with updated user
      await this.establishAdminSession();
    } catch (error: any) {
      console.error(`Error linking ${providerType}:`, error);
      
      if (error.message === "No email received from provider") {
        // Re-throw this specific case without transformation
        throw error;
      } else if (error.code === 'auth/credential-already-in-use') {
        throw new Error(`This ${providerType} account is already linked to another user`);
      } else if (error.code === 'auth/provider-already-linked') {
        throw new Error(`${providerType} is already linked to your account`);
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked. Please allow popups for this site');
      } else if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled');
      }
      
      throw error;
    }
  }

  // Method to establish admin session after authentication
  public async establishAdminSession(phone?: string): Promise<void> {
    await this.ensureAdminSession(true, phone);
  }

  public async updatePhoneNumber(e164Phone: string, recaptchaVerifier: any): Promise<any> {
    const user = this.auth?.currentUser;
    if (!user) throw new Error("No authenticated user");
    
    // Check if user already has a phone number
    const hasPhone = user.providerData?.some(p => p?.providerId === 'phone');
    
    if (hasPhone) {
      // User already has phone - use updatePhoneNumber
      const { PhoneAuthProvider } = await import("@firebase/auth");
      const phoneProvider = new PhoneAuthProvider(this.auth);
      return await phoneProvider.verifyPhoneNumber(e164Phone, recaptchaVerifier);
    } else {
      // User doesn't have phone - use linkWithPhoneNumber
      const { linkWithPhoneNumber } = await import("@firebase/auth");
      return await linkWithPhoneNumber(user, e164Phone, recaptchaVerifier);
    }
  }

  /**
   * Confirm the received SMS code to complete phone linking or update.
   * Ensures admin session once linking is complete.
   */
  public async confirmPhoneLinking(confirmationResult: any, code: string): Promise<FirebaseUser> {
    const user = this.auth?.currentUser;
    if (!user) throw new Error("No authenticated user");
    
    // Check if this is a PhoneAuthCredential verification ID (from updatePhoneNumber)
    // or a ConfirmationResult (from linkWithPhoneNumber)
    if (typeof confirmationResult === 'string') {
      // This is a verification ID from PhoneAuthProvider
      const { PhoneAuthProvider, updatePhoneNumber } = await import("@firebase/auth");
      const credential = PhoneAuthProvider.credential(confirmationResult, code);
      await updatePhoneNumber(user, credential);
      await this.ensureAdminSession(true);
      return user;
    } else {
      // This is a ConfirmationResult from linkWithPhoneNumber
      const userCredential = await confirmationResult.confirm(code);
      await this.ensureAdminSession(true);
      return userCredential.user as FirebaseUser;
    }
  }

  public getAuthProvider(): AuthProvider {
    return {
      login: this.handleLogIn,
      logout: this.handleLogOut,
      check: this.handleCheckAuth,
      onError: this.onError,
      getPermissions: this.getPermissions,
      getIdentity: this.getUserIdentity,
    };
  }

  private async ensureAdminSession(force: boolean = false, phone?: string): Promise<void> {
    if (!force && hasAuthCookies()) {
      try {
        await this.verifyAdminSession();
        return; // Cookies are valid, no need to refresh
      } catch (error) {
        authLogger.log('existing_session_invalid', {}, error as Error);
        // Fall through to refresh the session
      }
    }
    
    const user = this.auth?.currentUser ?? (await this.getFirebaseUser(this.isAuthInitialized));
    const token = await user?.getIdToken?.();
    if (!token) {
      authLogger.log('session_check_failed', { reason: 'no_firebase_token' });
      throw new Error("No Firebase token available");
    }
    
    try {
      const adminAuth = await this.authenticateAdminWithFirebaseToken(token, phone);
      // Proactively clear any previous tokens to avoid duplicates with different domain/path attributes
      deleteCookie('accessToken');
      deleteCookie('refreshToken');
      setCookiesForWildcardDomain({
        accessToken: adminAuth.accessToken,
        refreshToken: adminAuth.refreshToken,
      });
      // Persist access token expiry (buffer 60s) for preemptive refreshes in api()
      if (typeof window !== 'undefined' && typeof adminAuth.expiresIn === 'number') {
        const bufferSec = 60;
        const expiresAt = Date.now() + Math.max(0, adminAuth.expiresIn - bufferSec) * 1000;
        try {
          window.localStorage.setItem('accessTokenExpiresAt', String(expiresAt));
        } catch {}
      }
      authLogger.log('login_success', { type: 'session_established', expiresIn: adminAuth.expiresIn });
    } catch (error: any) {
      // If user needs onboarding (404), throw the error so handleCheckAuth can redirect
      if (error?.needsOnboarding || error?.status === 404) {
        authLogger.log('login_failed', { type: 'user_not_found', needsOnboarding: true });
        throw error;
      }
      authLogger.log('login_failed', { type: 'admin_token_exchange' }, error as Error);
      throw error;
    }
  }

  private async verifyAdminSession(): Promise<void> {
    try {
      // Make a simple API call to verify the session works
      // Using the same endpoint as user identity check
      await apiJson(`admin/${apiVersion}/me`);
    } catch (error: any) {
      // Propagate the 404 status / needsOnboarding flag so
      // handleCheckAuth can route the user to /onboarding instead of
      // treating the missing-profile case as a generic auth failure and
      // bouncing them to /login. See getlst/getlost-admin#112 follow-up.
      if (error?.status === 404 || error?.needsOnboarding) {
        const err: any = new Error(
          error?.message ?? "Admin session verification failed",
        );
        err.status = 404;
        err.needsOnboarding = true;
        throw err;
      }
      throw new Error('Admin session verification failed');
    }
  }
}

/**
 * Single source of truth for "the backend says this user needs to be
 * routed to /onboarding instead of being treated as authenticated".
 *
 * The auth provider attaches both `status: 404` and `needsOnboarding: true`
 * to the error thrown by `authenticateAdminWithFirebaseToken` when the
 * monolith returns 404 for a Firebase UID that has no `users` row yet.
 * Phone login flows must call this helper instead of substring-matching
 * the error message — see getlst/getlost-admin#110 for the contract.
 */
export function isOnboardingRequired(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { status?: unknown; needsOnboarding?: unknown };
  return e.status === 404 || e.needsOnboarding === true;
}
